











































































































































































import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator';
import MixinDialog from '@/plugins/podolib/services/helper/mixins/dialog';

import { TEntity } from '@/plugins/podolib/services/entities/Entity';
import { TPatient } from '@/plugins/podolib/services/entities/EntityPatients';
import { ValidationProvider } from 'vee-validate/dist/types/components/Provider';
import { TForm } from '@/plugins/podolib/lib/types';
import { Severity } from '@sentry/types';

type TFields = {
  pathologyType: {
    value: string;
  };
  pathology: {
    value: string;
  };
  pathologyCustomName: {
    value: string;
  };
  pathologyStartingDate: {
    value: string;
    modal: boolean;
  };
};
type TPathologyTypes = {
  data: TPathologyType[];
  limit: number;
  skip: number;
  total: number;
};
type TPathologyType = {
  name: {
    en?: string;
    fr?: string;
    de?: string;
    it?: string;
  };
  _id: string;
};
type TPathologies = {
  data: TPathologyReference[];
  limit: number;
  skip: number;
  total: number;
};
type TPathology = {
  _id?: string;
  reference?: TPathologyReference;
  name?: string;
  sinceDate?: string;
};
type TPathologyReference = {
  name?: {
    en?: string;
    fr?: string;
    de?: string;
    it?: string;
  };
  customName?: string;
  type: string;
  _id: string;
};

@Component
export default class PatientPathologyAddOrEdit extends Mixins(MixinDialog) {
  $refs!: {
    observer: InstanceType<typeof ValidationProvider>;
    form: InstanceType<typeof Vue> & { reset: () => void };
    picker: any;
  };

  @Prop({ default: null }) private patient?: TEntity<TPatient> | null;

  // Edit mode
  duringPatientCreation = false;
  editMode = false;
  editPathology: TPathology | null = null;
  openEditCustomPathology = false;
  currentPathologyType = 'basic';

  // Globals
  dialog = false;
  form: TForm<TFields> = {
    fields: {
      pathologyType: {
        value: '',
      },
      pathology: {
        value: '',
      },
      pathologyCustomName: {
        value: '',
      },
      pathologyStartingDate: {
        value: '',
        modal: false,
      },
    },
    pending: false,
    error: null,
    success: false,
  };

  // All pathologies
  pathologyTypes!: TPathologyTypes;
  customPathologies!: TPathologies;
  //
  pathologies!: TPathologies;
  // Pathologies status
  havePathologies = false;

  // Current language for pathologies
  get language() {
    return this.$pdc.helper?.isAnExistingLanguage(this.$store.getters['pdc/doctor/lang']);
  }
  get hasCustomName() {
    return this.form.fields.pathology.value === 'other';
  }

  @Watch('dialog')
  onDialogChanged(state: boolean) {
    if (state) {
      // Reset flags
      this.form.success = false;
      this.form.error = null;
    }
  }

  @Watch('form.fields', { deep: true })
  onFormFieldsDeeplyChanged() {
    // To avoid flash css switch
    if (!this.dialog) return;
    // Reset flags
    this.form.success = false;
    this.form.error = null;
  }
  @Watch('form.fields.pathologyCustomName.value')
  onFormCustomNameChanged(val: string) {
    if (val && val !== '') {
      this.currentPathologyType = 'custom';
    }
  }
  @Watch('form.fields.pathology.value')
  onFormPathologyNameChanged(val: string) {
    if (this.form.fields.pathologyCustomName.value) this.clearCustomPathology();
  }

  // Fetch all pathologies if type is selected
  @Watch('form.fields.pathologyType.value', { deep: true })
  onFormFieldsPathologyTypeChanged(id: string) {
    if (id && id !== '') this.fetchPathologies(id);
  }
  // Fetch all pathologies if type is selected
  @Watch('form.fields.pathology.value')
  onFormFieldsPathologyChanged(val: string) {
    this.currentPathologyType = val === 'other' ? 'custom' : 'basic';
  }
  // Get pathology types
  async fetchPathologyTypes() {
    try {
      this.pathologyTypes = await this.$pdc.repository!.fetchRessource({
        ressource: 'pathologytypes',
        params: {
          $limit: 50,
          $select: [`name.${this.language}`],
        },
      });
    } catch (e) {
      console.log(e);
    }
  }
  // Get pathologies by type
  async fetchPathologies(id: string) {
    try {
      this.pathologies = await this.$pdc.repository!.fetchRessource({
        ressource: 'pathologies',
        params: {
          $limit: 50,
          type: id,
        },
      });
      this.pathologies.data.push({
        name: {
          en: 'Other...',
          fr: 'Autre...',
          de: 'Andere...',
          it: 'Altro...',
        },
        type: id,
        _id: 'other',
      });
      this.havePathologies = true;
    } catch (e) {
      this.havePathologies = false;
      console.log(e);
    }
  }

  // Choose if it's add or edit action
  async save() {
    if (this.editMode) {
      await this.edit();
    } else await this.add();
  }
  // Add pathology
  async add() {
    // Avoid duplication of the process
    if (this.form.pending) return;

    let data;

    // Check if pathology type has changed
    const pathologyTypeHasChange = this.editMode
      ? (!this.openEditCustomPathology && this.currentPathologyType === 'custom') ||
        (this.openEditCustomPathology && this.currentPathologyType === 'basic')
      : false;
    // Get data depending on the hasCustomName status

    const hasDate =
      this.form.fields?.pathologyStartingDate.value !== '' && this.form.fields?.pathologyStartingDate.value;
    if (this.currentPathologyType === 'custom') {
      data = hasDate
        ? {
            name: this.form.fields?.pathologyCustomName.value,
            sinceDate: this.formatDate(this.form.fields?.pathologyStartingDate.value),
          }
        : {
            name: this.form.fields?.pathologyCustomName.value,
          };
    } else {
      data = hasDate
        ? {
            reference: this.form.fields?.pathology?.value,
            sinceDate: this.formatDate(this.form.fields?.pathologyStartingDate.value),
          }
        : {
            reference: this.form.fields?.pathology?.value,
          };
    }

    // If is not on patient creation
    if (!this.duringPatientCreation) {
      const emitData = {
        pathology: data,
        editedPathology: this.editPathology,
      };
      this.$emit('selectedPathology', emitData);
      try {
        const isValid = await this.$refs.observer.validate();
        if (!isValid) return;
        // Reset flags
        this.form.success = false;
        this.form.error = null;
        // Set 'pending' flag
        this.form.pending = true;
        // Get data depending on the currentPathologyType status
        const addToSetData =
          this.currentPathologyType === 'custom' ? { 'infos.customPathologies': data } : { 'infos.pathologies': data };

        if (pathologyTypeHasChange) {
          const lastPathologyData = this.openEditCustomPathology
            ? {
                'infos.customPathologies': {
                  _id: this.editPathology!._id,
                },
              }
            : {
                'infos.pathologies': {
                  reference: this.editPathology!.reference!._id,
                },
              };
          try {
            this.$pdc.repository?.patchRessource({
              ressource: 'patients',
              params: {
                _id: this.patient!._id,
              },
              data: {
                $pull: lastPathologyData,
              },
            });
          } catch (e) {
            console.log(e);
          }
        }

        this.$pdc.repository?.patchRessource({
          ressource: 'patients',
          params: {
            _id: this.patient!._id,
          },
          data: {
            $addToSet: addToSetData,
          },
        });

        // Set 'success' flag
        this.form.success = true;
        this.close();
      } catch (e) {
        // Set 'error' flag
        console.log(e);
        this.form.error = e;
        // Sentry
        this.$sentry.captureMessage(e.message, Severity.Error);
      } finally {
        // Reset 'pending' flag
        this.form.pending = false;
      }
    } else {
      this.$emit('selectedPathology', data);
      this.close();
    }
  }
  // Edit pathology
  async edit() {
    if (this.form.pending) return;

    const pathologyTypeHasChange = this.editMode
      ? (!this.openEditCustomPathology && this.currentPathologyType === 'custom') ||
        (this.openEditCustomPathology && this.currentPathologyType === 'basic')
      : false;

    if (!this.duringPatientCreation && !pathologyTypeHasChange) {
      try {
        // Get data depending on the currentPathologyType status
        const pullData =
          this.currentPathologyType === 'custom'
            ? { 'infos.customPathologies': { name: this.editPathology?.name } }
            : { 'infos.pathologies': { reference: this.editPathology?.reference?._id } };

        // Remove selected pathology
        this.$pdc.repository?.patchRessource({
          ressource: 'patients',
          params: {
            _id: this.patient!._id,
          },
          data: {
            $pull: pullData,
          },
        });
        // Add selected pathology
        await this.add();
      } catch (e) {
        // Set 'error' flag
        this.form.error = e;
        // Sentry
        this.$sentry.captureMessage(e.message, Severity.Error);
      }
    } else {
      await this.add();
    }
  }

  // Ask if this pathology already exist for this patient
  checkIfPathologyIsUsed(id: string) {
    if (this.patient?.infos?.pathologies) {
      for (const pathology of this.patient.infos.pathologies) {
        if (pathology.reference._id === id) return true;
      }
    }
  }
  // Change '2000-01' into date format
  formatDate(date: string) {
    const hasDate = date !== '' && date;
    return hasDate ? this.$moment(`${date}-01 12:00:00`).format() : '';
  }
  // Remove custom pathology
  clearCustomPathology() {
    if (this.form.fields.pathology.value !== 'other') this.currentPathologyType = 'basic';
    this.form.fields.pathologyCustomName.value = '';
  }

  // Open dialogs
  openAdd() {
    this.editMode = false;
    this.duringPatientCreation = false;
    this.dialog = true;
  }
  // Edit
  async openEdit(pathology: TPathology) {
    this.editMode = true;
    this.duringPatientCreation = false;
    this.editPathology = pathology;
    await this.getEditInformation();
    this.dialog = true;
  }
  // On patient creation
  openAddDuringPatientCreation(onPatientEdition: boolean = true) {
    this.editMode = false;
    this.duringPatientCreation = onPatientEdition;
    this.dialog = true;
  }
  async openEditDuringPatientCreation(pathology: TPathology) {
    this.editMode = true;
    this.duringPatientCreation = true;
    this.editPathology = pathology;
    await this.getEditInformation();
    this.dialog = true;
  }
  // Get pathology information
  async getEditInformation() {
    if (this.editPathology?.reference) {
      this.currentPathologyType = 'basic';
      // Get pathologies
      await this.fetchPathologyTypes();
      await this.fetchPathologies(this.editPathology?.reference.type);
      // Type selected
      this.form.fields.pathologyType.value = this.editPathology?.reference.type;
      // Pathology selected
      if (this.form.fields.pathologyType.value && this.form.fields.pathologyType.value !== '') {
        this.form.fields.pathology.value = this.editPathology?.reference._id;
      }
      // Custom pathology filled
    } else if (this.editPathology?.name) {
      this.openEditCustomPathology = true;
      this.currentPathologyType = 'custom';
      this.form.fields.pathologyCustomName.value = this.editPathology.name;
    }
    // SinceDate filled
    if (this.editPathology?.sinceDate) {
      // Get only year and month (2000-01)
      this.form.fields.pathologyStartingDate.value = this.editPathology?.sinceDate.substr(0, 7);
    }
  }

  close() {
    this.clear();
  }
  clear() {
    this.$refs.form.reset();
    requestAnimationFrame(() => {
      (this.$refs.observer as Vue & { reset: () => void }).reset();
    });
    this.dialog = false;
    this.editPathology = null;
    this.openEditCustomPathology = false;
    this.duringPatientCreation = false;
    this.currentPathologyType = 'basic';
    this.cleanValue();
  }
  cleanValue() {
    this.form.fields = {
      pathologyType: {
        value: '',
      },
      pathology: {
        value: '',
      },
      pathologyCustomName: {
        value: '',
      },
      pathologyStartingDate: {
        value: '',
        modal: false,
      },
    };
  }

  mounted() {
    this.cleanValue();
    this.fetchPathologyTypes();
    this.$pdc.bus!.on('patient-creation:pathology:add', this.openAddDuringPatientCreation);
    this.$pdc.bus!.on('patient-creation:pathology:edit', this.openEditDuringPatientCreation);
    this.$pdc.bus!.on('pathology:add', this.openAdd);
    this.$pdc.bus!.on('pathology:edit', this.openEdit);
  }
  beforeDestroy() {
    this.$pdc.bus!.off('pathology:add', this.openAdd);
    this.$pdc.bus!.off('pathology:edit', this.openEdit);
  }
}
